import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { analyticsApi } from '../../services/api';

interface CategoryTrendData {
  month: string;
  amount: number;
}

interface CategoryTrendsData {
  category_name: string;
  color: string;
  data: CategoryTrendData[];
}

interface TopCategoriesTrendsData {
  month: string;
  [key: string]: number | string; // Dynamic category names as keys
}

const TopCategoriesTrendsChart: React.FC = () => {
  const [data, setData] = useState<TopCategoriesTrendsData[]>([]);
  const [categories, setCategories] = useState<CategoryTrendsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yAxisDomain, setYAxisDomain] = useState<[number, number | 'auto']>([0, 'auto' as const]);
  const [hasClippedData, setHasClippedData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get category trends for 12 months, top 4 categories
        const response = await analyticsApi.getCategoryTrends(12, 4);
        setCategories(response.categories);
        
        // Transform data for recharts - combine all categories by month
        if (response.categories.length > 0) {
          // Get all unique months from all categories
          const allMonths = Array.from(
            new Set(
              response.categories.flatMap((category: CategoryTrendsData) => 
                category.data.map(item => item.month)
              )
            )
          ) as string[];
          
          allMonths.sort((a: string, b: string) => {
            // Sort months chronologically
            const [monthA, yearA] = a.split(' ');
            const [monthB, yearB] = b.split(' ');
            if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
            
            const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
          });

          // Create combined data structure
          const transformedData = allMonths.map((month) => {
            const dataPoint: TopCategoriesTrendsData = { month };
            
            response.categories.forEach((category: CategoryTrendsData) => {
              const monthData = category.data.find(item => item.month === month);
              dataPoint[category.category_name] = monthData ? monthData.amount : 0;
            });
            
            return dataPoint;
          });
          
          setData(transformedData);
          
          // Calculate outlier-resistant Y-axis domain
          const allValues: number[] = [];
          transformedData.forEach(dataPoint => {
            categories.forEach(category => {
              const value = dataPoint[category.category_name] as number;
              if (value > 0) allValues.push(value);
            });
          });
          
          if (allValues.length > 0) {
            allValues.sort((a, b) => a - b);
            
            // Calculate percentiles for outlier detection
            const q1Index = Math.floor(allValues.length * 0.25);
            const q3Index = Math.floor(allValues.length * 0.75);
            const q1 = allValues[q1Index];
            const q3 = allValues[q3Index];
            const iqr = q3 - q1;
            
            // Define outlier thresholds
            const upperBound = q3 + 1.5 * iqr;
            
            // Use 95th percentile as max if outliers exist, otherwise use actual max
            const p95Index = Math.floor(allValues.length * 0.95);
            const p95 = allValues[p95Index];
            const maxValue = allValues[allValues.length - 1];
            
            // Use capped domain if there are significant outliers
            const hasOutliers = maxValue > upperBound && maxValue > p95 * 1.5;
            const yMax = hasOutliers ? Math.ceil(p95 * 1.1) : Math.ceil(maxValue * 1.1);
            
            
            setYAxisDomain([0, yMax]);
            setHasClippedData(hasOutliers);
          } else {
            setYAxisDomain([0, 'auto' as const]);
          }
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load top categories trends data');
        console.error('Error fetching top categories trends data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const yMax = typeof yAxisDomain[1] === 'number' ? yAxisDomain[1] : Number.MAX_SAFE_INTEGER;
      
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: 2,
            maxWidth: 300
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {label}
          </Typography>
          {payload
            .filter((entry: any) => entry.value > 0)
            .sort((a: any, b: any) => b.value - a.value)
            .map((entry: any, index: number) => {
              const isClipped = entry.value > yMax * 0.9; // Close to the cap
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: entry.color }}>
                    {entry.dataKey}: {formatCurrency(entry.value)}
                  </Typography>
                  {isClipped && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'warning.main' }}>
                      ⚠️
                    </Typography>
                  )}
                </Box>
              );
            })}
          {hasClippedData && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
              Some high values may be clipped for better readability
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (data.length === 0 || categories.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Top 4 Categories - 12 Month Trends
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
          <Typography variant="body1" color="text.secondary">
            No category trends data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
        Top 4 Categories - 12 Month Trends
      </Typography>
      
      <Box sx={{ height: { xs: 250, sm: 300 }, mt: 2, px: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month"
              stroke="#666"
              fontSize={11}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="#666"
              fontSize={12}
              width={60}
              domain={yAxisDomain}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            {categories.map((category, index) => (
              <Line
                key={category.category_name}
                type="monotone"
                dataKey={category.category_name}
                stroke={category.color}
                strokeWidth={2.5}
                dot={{ fill: category.color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: category.color, strokeWidth: 2 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Outlier indicator */}
      {hasClippedData && (
        <Box sx={{ 
          mt: 1, 
          p: 1, 
          backgroundColor: 'info.light', 
          borderRadius: 1, 
          borderLeft: '4px solid',
          borderColor: 'info.main'
        }}>
          <Typography variant="caption" sx={{ color: 'info.dark', fontSize: '0.75rem' }}>
            ℹ️ Chart scale optimized for readability - some extreme values may not be visible
          </Typography>
        </Box>
      )}

      {/* Category summary */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.8rem' }}>
          12-Month Totals:
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 1,
          maxHeight: '80px',
          overflowY: 'auto'
        }}>
          {categories.map((category) => {
            const totalSpending = category.data.reduce((sum, item) => sum + item.amount, 0);
            return (
              <Box
                key={category.category_name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'grey.50',
                  fontSize: '0.75rem',
                  minWidth: 0, // Allow text to truncate
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: category.color,
                    borderRadius: '50%',
                    flexShrink: 0
                  }}
                />
                <Typography 
                  variant="caption"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                  title={`${category.category_name}: ${formatCurrency(totalSpending)}`}
                >
                  {category.category_name}: {formatCurrency(totalSpending)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default TopCategoriesTrendsChart;